import type { Deal, DealStage } from "../../lib/types";
import { STAGE_REQUIREMENTS, getNextStage } from "./stageRequirements";

/**
 * Returns a short human-readable string describing what needs to happen
 * for a deal to advance to the next stage.
 *
 * Used on pipeline cards (short mode) and deal detail hero (full mode).
 */

interface NextActionItem {
  label: string;
  met: boolean;
  required: boolean;
}

export interface NextActionResult {
  /** Short one-liner for pipeline cards */
  summary: string;
  /** Next stage name */
  nextStageName: string | null;
  /** Full checklist items (for deal detail hero) */
  items: NextActionItem[];
  /** How many total required items */
  totalRequired: number;
  /** How many required items are done */
  completedRequired: number;
  /** Is the deal ready to advance? */
  canAdvance: boolean;
}

/** Fallback next-action text when no formal requirements exist */
const STAGE_NEXT_ACTIONS: Partial<Record<DealStage, string>> = {
  // Drone
  pending_asm_review: "Waiting for ASM review",
  appointment_received: "Schedule drone flight",
  drone_scheduled: "Complete drone flight",
  drone_complete: "Submit for report processing",
  report_in_progress: "Waiting for report from Jeremy",
  qc_review: "Waiting for Jennifer QC review",
  report_approved: "Convert to lead",
  needs_climb_inspection: "Authorize climb inspection",
  insufficient_damage: "Notify homeowner",

  // Leads
  initial_contact: "Schedule presentation",
  presentation_scheduled: "Complete presentation",
  follow_up: "Follow up with homeowner",
  waiting: "Waiting for homeowner response",
  no_thanks: "Lead declined — rework or close",

  // Claims
  assign_roof_specialist: "Assign a roof specialist",
  pre_inspection_ready: "Complete pre-inspection & set adjuster date",
  pre_inspection_complete: "Schedule adjuster meeting",
  adjuster_meeting_ready: "Complete adjuster meeting",
  adjuster_meeting_scheduled: "Complete adjuster meeting",
  waiting_on_claims: "Waiting for insurance decision",

  // Reinspections
  partial_approval: "Begin reinspection prep",
  docs_and_contingency: "Gather reinspection documents",
  reinspection_ready: "Submit reinspection to insurance",
  reinspection_filed: "Waiting for insurance response",

  // Builds
  install_meeting: "Complete install meeting",
  build_submitted: "Waiting for build approval (Chad or Robert, on the Build Approvals board)",
  schedule_build: "Schedule the build",
  build_scheduled: "Complete the build",

  // Closing
  roof_complete: "Complete trades & punch list",
  trades_in_progress: "Finish remaining trades",
  trades_complete: "Collect final payment",
  collect_payment: "Collect remaining balance",
  commission_ready: "Submit for commission review",
  commission_approved: "Commission approved — awaiting payment",
  commission_paid: "Register warranty & close out",
  closed_won: "Deal complete",
  closed_lost: "Deal closed",
  cancelled: "Deal cancelled",
};

/**
 * Get the next action for a deal — checks formal requirements first,
 * falls back to stage-based descriptions.
 */
export function getNextAction(deal: Partial<Deal> & { stage: DealStage | null }): NextActionResult {
  const currentStage = deal.stage;
  if (!currentStage) {
    return { summary: "Unknown stage", nextStageName: null, items: [], totalRequired: 0, completedRequired: 0, canAdvance: false };
  }

  const nextStage = getNextStage(currentStage);
  const nextStageName = nextStage ? nextStage.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : null;

  // Check if there are formal requirements for the next stage
  if (nextStage && STAGE_REQUIREMENTS[nextStage]) {
    const reqs = STAGE_REQUIREMENTS[nextStage]!;
    const items: NextActionItem[] = reqs.map((r) => ({
      label: r.label,
      met: r.check(deal as Deal),
      required: r.required,
    }));

    const requiredItems = items.filter((i) => i.required);
    const totalRequired = requiredItems.length;
    const completedRequired = requiredItems.filter((i) => i.met).length;
    const canAdvance = requiredItems.every((i) => i.met);

    // Build summary from first unmet required item
    const firstUnmet = items.find((i) => i.required && !i.met);
    const summary = firstUnmet
      ? `Next: ${firstUnmet.label}`
      : canAdvance
        ? `Ready to advance`
        : STAGE_NEXT_ACTIONS[currentStage] ?? "Continue processing";

    return { summary, nextStageName, items, totalRequired, completedRequired, canAdvance };
  }

  // No formal requirements — use fallback text
  const fallback = STAGE_NEXT_ACTIONS[currentStage] ?? "Continue processing";
  return {
    summary: fallback,
    nextStageName,
    items: [],
    totalRequired: 0,
    completedRequired: 0,
    canAdvance: false,
  };
}
